import { Grid, Skeleton, styled, Tooltip } from '@mui/material';
import React from 'react';
import HrCard from 'routes/HumanResourcesModule/components/HrCard';
import { RetrospectiveT } from '../../helpers/types';
import {
    RetrospectivePayComponentSalaryMassData,
    RetrospectiveSalaryMassData,
    useGetRetrospectivePayComponentSalaryMassData,
    useGetRetrospectiveSalaryMassData,
    useGetRetrospectiveUniqueAgentsData,
} from '../helpers/retrospectiveData';
import Flex from 'ui/Flex/Flex';
import InfoIcon from '@mui/icons-material/Info';
import { StyPaper } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/PayConstants';
import {
    StyBlackBoldText,
    StyText,
    StyPrimaryText,
} from '../helpers/StyTypography';
import { SalaryMassTotals } from './SalaryMassTotals';
import { SalaryMassEvolution } from './SalaryMassEvolution';
import {
    getAmounts,
    getRemuneratedEffectiveRange,
} from './helpers/dataHelpers';
import { formatFloatValue } from 'helpers/formatHelper';
import { useTranslation } from 'react-i18next';

const StyHrCard = styled(HrCard)(() => ({
    '.MuiCardHeader-action': { mr: 0 },
    height: '100%',
}));
const TotalsAndEvolutionAmounts = ({
    retrospective,
    data,
    payComponentData,
}: {
    retrospective: RetrospectiveT;
    data: RetrospectiveSalaryMassData;
    payComponentData: RetrospectivePayComponentSalaryMassData;
}): React.ReactElement => {
    return (
        <Grid
            container
            minHeight="25rem"
            spacing={2}
            justifyContent="space-between"
        >
            <Grid item xs={12}>
                <Flex
                    justifyContent="space-between"
                    direction="column"
                    height="100%"
                >
                    <SalaryMassTotals
                        data={data}
                        exercice={retrospective.exercice}
                    />
                    <SalaryMassEvolution
                        retrospective={retrospective}
                        data={payComponentData}
                    />
                </Flex>
            </Grid>
        </Grid>
    );
};

const AnnualAverageAmounts = ({
    retrospective,
    data,
}: {
    retrospective: RetrospectiveT;
    data: RetrospectiveSalaryMassData;
}): React.ReactElement => {
    const { t } = useTranslation();
    const [startDate, endDate] = getRemuneratedEffectiveRange({
        exercice: retrospective.exercice,
    });
    const { uniqueAgentsCount, uniqueAgentsCountWithAmounts, etp, etpt } =
        getAmounts({ data });
    return (
        <>
            <StyPaper
                sx={{
                    height: '100%',
                    padding: '1rem',
                    marginBottom: '0.2rem',
                }}
            >
                <Grid container gap={2}>
                    <Grid item xs={12}>
                        <Flex justifyContent="flex-start" gap={1}>
                            <StyBlackBoldText>
                                {t(
                                    'humanResources.retrospective.salaryMass.annualAverageEffectiveTitle',
                                )}
                            </StyBlackBoldText>
                            <Tooltip
                                title={t(
                                    'humanResources.retrospective.salaryMass.annualAverageEffectiveTooltip',
                                )}
                                key="button-element"
                            >
                                <InfoIcon
                                    color="primary"
                                    sx={{ fontSize: 16 }}
                                />
                            </Tooltip>
                        </Flex>
                    </Grid>
                    <Grid item xs={12}>
                        <Flex justifyContent="space-between">
                            <StyText>
                                {t(
                                    'humanResources.retrospective.salaryMass.annualAverageEffective',
                                )}
                            </StyText>
                            <StyPrimaryText>
                                {formatFloatValue(uniqueAgentsCount)}
                            </StyPrimaryText>
                        </Flex>
                    </Grid>
                    <Grid item xs={12}>
                        <Flex justifyContent="space-between">
                            <Flex justifyContent="flex-start" gap={1}>
                                <StyText>{t('humanResources.etp')}</StyText>
                                <Tooltip
                                    title={t(
                                        'humanResources.retrospective.etpTooltip',
                                    )}
                                    key="button-element"
                                >
                                    <InfoIcon
                                        color="primary"
                                        sx={{ fontSize: 16 }}
                                    />
                                </Tooltip>
                            </Flex>
                            <StyPrimaryText>
                                {formatFloatValue(etp)}
                            </StyPrimaryText>
                        </Flex>
                    </Grid>
                    <Grid item xs={12}>
                        <Flex justifyContent="space-between">
                            <Flex justifyContent="flex-start" gap={1}>
                                <StyText>{t('humanResources.etpt')}</StyText>
                                <Tooltip
                                    title={t(
                                        'humanResources.retrospective.etptTooltip',
                                    )}
                                    key="button-element"
                                >
                                    <InfoIcon
                                        color="primary"
                                        sx={{ fontSize: 16 }}
                                    />
                                </Tooltip>
                            </Flex>
                            <StyPrimaryText>
                                {formatFloatValue(etpt)}
                            </StyPrimaryText>
                        </Flex>
                    </Grid>
                    <Grid item xs={12}>
                        <Flex justifyContent="space-between">
                            <StyText>
                                {t(
                                    'humanResources.retrospective.salaryMass.remuneratedEffective',
                                    {
                                        startDate,
                                        endDate,
                                    },
                                )}
                            </StyText>
                            <StyPrimaryText>
                                {formatFloatValue(uniqueAgentsCountWithAmounts)}
                            </StyPrimaryText>
                        </Flex>
                    </Grid>
                </Grid>
            </StyPaper>
        </>
    );
};

export const SalaryMass = ({
    retrospective,
}: {
    retrospective: RetrospectiveT;
}): React.ReactElement => {
    const { t } = useTranslation();
    const { data: salaryMassResponse, isLoading: isSalaryMassResponseLoading } =
        useGetRetrospectiveSalaryMassData(retrospective?.id ?? 0);
    const {
        data: uniqueAgentsResponse,
        isLoading: isUniqueAgentsResponseLoading,
    } = useGetRetrospectiveUniqueAgentsData(retrospective?.id ?? 0);

    let totalAmountsData: RetrospectiveSalaryMassData = {
        brutTotal: 0,
        employerCostsTotal: 0,
        salaryMassCurrentYear: 0,
        salaryMassPreviousYear: 0,
        totalLastMonthCurrentYear: 0,
        totalLastMonthPreviousYear: 0,
        etp: 0,
        etpt: 0,
        uniqueAgentsCount: 0,
        uniqueAgentsCountWithAmounts: 0,
    };
    const salaryMass = salaryMassResponse ?? {};
    totalAmountsData = Object.keys(totalAmountsData).reduce((acc, key) => {
        acc[key as keyof RetrospectiveSalaryMassData] =
            (salaryMass as RetrospectiveSalaryMassData)[
                key as keyof RetrospectiveSalaryMassData
            ] ?? 0;
        return acc;
    }, {} as RetrospectiveSalaryMassData);
    totalAmountsData.uniqueAgentsCount =
        uniqueAgentsResponse === undefined
            ? 0
            : uniqueAgentsResponse.uniqueAgentsCount;

    const {
        data: payComponentSalaryMassResponse,
        isLoading: isPayComponentSalaryMassResponseLoading,
    } = useGetRetrospectivePayComponentSalaryMassData(retrospective?.id ?? 0);
    let payComponentTotalAmountsData: RetrospectivePayComponentSalaryMassData =
        {
            payComponentSalaryMassCurrentYear: 0,
            payComponentSalaryMassPreviousYear: 0,
            payComponentTotalLastMonthCurrentYear: 0,
            payComponentTotalLastMonthPreviousYear: 0,
        };
    const payComponentSalaryMass = payComponentSalaryMassResponse ?? {};
    payComponentTotalAmountsData = Object.keys(
        payComponentTotalAmountsData,
    ).reduce((acc, key) => {
        acc[key as keyof RetrospectivePayComponentSalaryMassData] =
            (payComponentSalaryMass as RetrospectivePayComponentSalaryMassData)[
                key as keyof RetrospectivePayComponentSalaryMassData
            ] ?? 0;
        return acc;
    }, {} as RetrospectivePayComponentSalaryMassData);

    return (
        <>
            {isSalaryMassResponseLoading ||
            isPayComponentSalaryMassResponseLoading ||
            isUniqueAgentsResponseLoading ? (
                <Skeleton
                    variant="rectangular"
                    sx={{ width: '100%', height: '29rem', borderRadius: '4px' }}
                />
            ) : (
                <StyHrCard
                    title={t(
                        'humanResources.retrospective.salaryMass.titleSection',
                    )}
                >
                    <Grid
                        container
                        minHeight="25rem"
                        height="100%"
                        width="100%"
                        spacing={2}
                    >
                        <>
                            <Grid item xs={6} height="100%">
                                <TotalsAndEvolutionAmounts
                                    retrospective={retrospective}
                                    data={totalAmountsData}
                                    payComponentData={
                                        payComponentTotalAmountsData
                                    }
                                />
                            </Grid>
                            <Grid item xs={6} minHeight="25rem">
                                <AnnualAverageAmounts
                                    retrospective={retrospective}
                                    data={totalAmountsData}
                                />
                            </Grid>
                        </>
                    </Grid>
                </StyHrCard>
            )}
        </>
    );
};
